import React from "react";
import { Link } from "react-router-dom";
import {
    Mail, Gem, Shield, Wrench, ChevronDown,
    Sparkles, Calendar, Baby, Stethoscope, MessageCircle,
    HeartHandshake, ArrowRight
} from "lucide-react";
import { isIOSAppWebView } from "../lib/platform";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

/* ---------- Reusable bits ---------- */

const FAQ = ({ q, children, testId }) => (
    <details
        className="group bg-white border border-femcare-border rounded-2xl overflow-hidden hover:border-femcare-pink/50 transition-colors"
        data-testid={testId}
    >
        <summary className="cursor-pointer list-none p-5 md:p-6 flex items-center justify-between gap-4 font-medium text-femcare-purple">
            <span className="font-serif text-lg leading-snug pr-4">{q}</span>
            <ChevronDown className="w-5 h-5 text-femcare-pink shrink-0 transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-5 md:px-6 pb-6 text-femcare-purple/80 leading-relaxed [&_a]:text-femcare-pink [&_a]:underline [&_a]:decoration-femcare-pink/30 [&_a]:underline-offset-4 [&_a:hover]:decoration-femcare-pink [&_b]:text-femcare-purple [&_b]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:mt-2">
            {children}
        </div>
    </details>
);

const Callout = ({ children }) => (
    <div className="mt-4 bg-femcare-petal/50 border-l-4 border-femcare-pink rounded-r-xl p-4 text-sm text-femcare-purple/90">
        {children}
    </div>
);

const Section = ({ id, icon: Icon, title, children }) => (
    <section id={id} className="scroll-mt-28 space-y-3" data-testid={`support-section-${id}`}>
        <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-full bg-femcare-petal flex items-center justify-center">
                <Icon className="w-4 h-4 text-femcare-pink" />
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-femcare-purple">{title}</h2>
        </div>
        <div className="space-y-3">{children}</div>
    </section>
);

const QuickCard = ({ to, icon: Icon, title, sub, testId }) => (
    <a
        href={to}
        className="group bg-white border border-femcare-border hover:border-femcare-pink/60 hover:shadow-xl hover:shadow-femcare-pink/10 rounded-2xl p-5 flex items-start gap-4 transition"
        data-testid={testId}
    >
        <span className="w-11 h-11 rounded-full bg-gradient-to-br from-femcare-pink to-femcare-accent text-white flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1">
            <h3 className="font-serif text-lg text-femcare-purple flex items-center gap-1.5">
                {title}
                <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition" />
            </h3>
            <p className="text-sm text-femcare-purple/65 mt-0.5">{sub}</p>
        </div>
    </a>
);

/* ---------- Page ---------- */

export default function Support() {
    const iOSApp = isIOSAppWebView();
    useReveal();

    const sections = [
        ["start", "Getting started"],
        ["cycle", "Cycle & fertility"],
        ["pregnancy", "Pregnancy mode"],
        ["daughter", "Daughter Care"],
        ["ai", "AI Assistant"],
        ["billing", "Premium & billing"],
        ["privacy", "Privacy & data"],
        ["troubleshooting", "Troubleshooting"],
        ["contact", "Contact us"],
    ];

    return (
        <div className="bg-femcare-cream" data-testid="support-page">
            <PageHero
                testId="support-hero"
                overline="Help Center"
                title={<>Support &amp;<br /><span className="italic font-light">Help Center.</span></>}
                subtitle="Answers to common questions about cycle tracking, fertility, pregnancy mode, premium subscriptions, Daughter Care, privacy, and how to contact our care team."
                size="lg"
            />

            {/* QUICK ACTIONS */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="support-quick">
                    <QuickCard to="#contact" icon={Mail} title="Contact our care team" sub="support@femcare.life" testId="quick-contact" />
                    <QuickCard to="#billing" icon={Gem} title="Premium & billing" sub="Subscriptions, refunds, restore" testId="quick-billing" />
                    <QuickCard to="#privacy" icon={Shield} title="Privacy & data" sub="Delete account, export data" testId="quick-privacy" />
                    <QuickCard to="#troubleshooting" icon={Wrench} title="Troubleshooting" sub="Reminders, sync, login" testId="quick-troubleshoot" />
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24">
                {/* STICKY TOC */}
                <aside className="lg:col-span-3 hidden lg:block">
                    <nav className="sticky top-24 space-y-1 text-sm" data-testid="support-toc">
                        <p className="text-xs uppercase tracking-[0.25em] text-femcare-accent mb-3">Browse topics</p>
                        {sections.map(([id, label]) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                className="block py-1.5 text-femcare-purple/65 hover:text-femcare-pink border-l-2 border-transparent hover:border-femcare-pink pl-3 transition"
                            >
                                {label}
                            </a>
                        ))}
                    </nav>
                </aside>

                {/* CONTENT */}
                <article className="lg:col-span-9 space-y-14">

                    {/* GETTING STARTED */}
                    <Section id="start" icon={Sparkles} title="Getting started">
                        <FAQ q="How do I create an account?" testId="faq-create-account">
                            <p>Open the app, tap <b>Sign up</b>, and choose between Email signup or Google Sign-in. You'll be asked for a few basics (date of birth, last period date) so Femcare can personalise predictions from day one. You can always update these later under <b>Profile · Edit</b>.</p>
                        </FAQ>
                        <FAQ q="What does the home screen show me?">
                            <p>Your home screen surfaces:</p>
                            <ul>
                                <li><b>Cycle calendar</b> — colour-coded days (pink = period, green = fertile window, purple = ovulation).</li>
                                <li><b>Daily log card</b> — quick tap to log mood, symptoms, flow, BBT, water, sleep.</li>
                                <li><b>Daughter Care</b> — your daughter's wellness summary (if you have the add-on).</li>
                                <li><b>Wellness report</b> — your latest AI-generated insights and downloadable PDF.</li>
                            </ul>
                        </FAQ>
                        <FAQ q="Do I need to log every day?">
                            <p>No — but logging consistently makes predictions much more accurate. Even logging just your <b>period start</b> and a couple of moods/symptoms per week dramatically improves Femcare's understanding of your cycle.</p>
                        </FAQ>
                    </Section>

                    {/* CYCLE & FERTILITY */}
                    <Section id="cycle" icon={Calendar} title="Cycle & fertility">
                        <FAQ q="How does Femcare predict my cycle?">
                            <p>Femcare uses the calendar method by default, learning from your <b>last period start date</b> and your <b>average cycle length</b> across the last 3 months. When you also log basal body temperature (BBT) and ovulation tests, predictions sharpen using a hybrid symptothermal model.</p>
                        </FAQ>
                        <FAQ q="What is the fertile window?">
                            <p>The fertile window is typically <b>5 days before</b> ovulation and <b>1 day after</b>. This is when conception is most likely. Femcare highlights these days in green on your calendar and sends a reminder on the most fertile day.</p>
                        </FAQ>
                        <FAQ q="How accurate is the ovulation prediction?">
                            <p>Standard predictions assume a 14-day luteal phase. For higher accuracy:</p>
                            <ul>
                                <li>Track <b>basal body temperature</b> daily (Log tab)</li>
                                <li>Log <b>cervical mucus</b> observations</li>
                                <li>Log <b>ovulation test results</b> (LH surge)</li>
                                {iOSApp ? (
                                    <li>Connect <b>Apple Health</b> (Premium)</li>
                                ) : (
                                    <li>Connect <b>Apple Health</b> or <b>Health Connect</b> (Premium)</li>
                                )}
                            </ul>
                        </FAQ>
                        <FAQ q="I have irregular cycles — does Femcare still work for me?">
                            <p>Yes. Femcare detects irregularity and switches to a wider, probabilistic fertile window instead of a single ovulation day. If your cycle length varies more than 7 days month-to-month, we recommend logging BBT + ovulation tests for the most reliable predictions.</p>
                        </FAQ>
                        <FAQ q="Can I use Femcare for contraception?">
                            <Callout><b>Important.</b> Femcare is a wellness app, not a contraceptive device. Predictions can be wrong, especially for irregular cycles. Please talk to a qualified clinician before relying on cycle-tracking for contraception.</Callout>
                        </FAQ>
                    </Section>

                    {/* PREGNANCY MODE */}
                    <Section id="pregnancy" icon={Baby} title="Pregnancy mode">
                        <FAQ q="How do I switch to Pregnancy Mode?">
                            <p>Open <b>Profile · Your status</b>, toggle on <b>Pregnancy mode</b>, and enter your last menstrual period (LMP) date. Femcare will calculate your estimated due date and switch the home screen to a week-by-week pregnancy view.</p>
                        </FAQ>
                        <FAQ q="What features come with Pregnancy mode?">
                            <ul>
                                <li>Week-by-week development updates for you and baby</li>
                                <li>Symptom tracker</li>
                                <li>Built-in <b>contraction timer</b></li>
                                <li>Customisable <b>Birth plan</b> exportable as PDF</li>
                                <li>Kick counter and weight log</li>
                            </ul>
                        </FAQ>
                        <FAQ q="How do I switch back to cycle tracking after pregnancy?">
                            <p>Go to <b>Profile · Your status</b> and toggle <b>Pregnancy mode</b> off. You'll be asked to enter your first post-pregnancy period start date so cycle predictions reset cleanly.</p>
                        </FAQ>
                    </Section>

                    {/* DAUGHTER CARE */}
                    <Section id="daughter" icon={HeartHandshake} title="Daughter Care">
                        <FAQ q="What is Daughter Care?">
                            <p>Daughter Care is a premium add-on that lets parents (and teens with privacy) track a daughter's wellness — age-based puberty awareness, period prep, teen FemCare, and adult-daughter fertility support. Profiles can stay private; parents see only what the teen chooses to share.</p>
                        </FAQ>
                        <FAQ q="How do I add my daughter's profile?">
                            <p>You must be a Premium subscriber <b>and</b> have the Daughter Care add-on. Then:</p>
                            <ul>
                                <li>Open <b>Daughter Care</b> from the home screen.</li>
                                <li>Tap <b>Add</b> in the top-right (or "Add your first profile" in the empty state).</li>
                                <li>Enter her name, date of birth, and choose a privacy mode.</li>
                            </ul>
                        </FAQ>
                        <FAQ q="Can my daughter use her own phone?">
                            <p>Yes — generate a <b>6-character pairing code</b> from Daughter Care and ask her to enter it on her own device's Femcare app. She'll then have her own login with privacy-protected logging; you'll see only the data she chooses to share.</p>
                        </FAQ>
                        <FAQ q="Daughter Care Yearly says it needs Premium Yearly?">
                            <p>That's correct — the yearly add-on only pairs with a Premium <b>Yearly</b> plan. If you're on Premium Monthly, either pick the <b>Monthly Daughter Care</b> add-on, or upgrade your main plan to Yearly first.</p>
                        </FAQ>
                    </Section>

                    {/* AI ASSISTANT */}
                    <Section id="ai" icon={MessageCircle} title="AI Care Assistant">
                        <FAQ q="What can I ask the AI assistant?">
                            <p>Anything women's-health related — cycle questions, fertility tips, pregnancy concerns, PCOS, menopause, contraception, symptom interpretation. The assistant is trained on clinician-reviewed content and is intended for education and wellness — not medical advice.</p>
                        </FAQ>
                        <FAQ q="Is the voice input private?">
                            {iOSApp ? (
                                <p>Yes. Voice is transcribed on-device using iOS Speech recognition, and only the resulting text is sent over an encrypted channel. We never store raw audio.</p>
                            ) : (
                                <p>Yes. Voice is transcribed on-device when supported (iOS Speech, Android SpeechRecognizer) and only the resulting text is sent over an encrypted channel. We never store raw audio.</p>
                            )}
                        </FAQ>
                        <FAQ q="Is the AI a medical device?">
                            <Callout><b>No.</b> The Femcare AI Assistant is for educational and wellness guidance only and should not be used to diagnose, treat, or prevent any disease. Always consult a qualified clinician for medical advice.</Callout>
                        </FAQ>
                    </Section>

                    {/* PREMIUM & BILLING */}
                    <Section id="billing" icon={Gem} title="Premium & billing">
                        <FAQ q="What's included in Premium?">
                            <ul>
                                <li>AI Care Assistant with voice input</li>
                                {iOSApp ? (
                                    <li>Wearable Sync (Apple Health)</li>
                                ) : (
                                    <li>Wearable Sync (Apple Health, Health Connect)</li>
                                )}
                                <li>Advanced health trends &amp; analytics</li>
                                <li>PDF wellness reports you can save or share</li>
                                <li>Personalised reminders &amp; smart notifications</li>
                                <li>Ad-free experience</li>
                            </ul>
                        </FAQ>
                        <FAQ q="How much does Premium cost?">
                            {iOSApp ? (
                                <p>You can see your region's current pricing on the <b>Premium</b> screen inside the app. Prices vary by region/currency and are set by the App Store.</p>
                            ) : (
                                <>
                                    <ul>
                                        <li><b>India:</b> ₹299/month or ₹999/year (Save 72%)</li>
                                        <li><b>Rest of world:</b> $12.99/month or $24.99/year (Save 84%)</li>
                                        <li><b>Daughter Care add-on:</b> ₹99/$4.99 monthly · ₹699/$9.99 yearly</li>
                                    </ul>
                                    <p className="mt-2">Prices may vary slightly by region/currency in your local app store.</p>
                                </>
                            )}
                        </FAQ>
                        <FAQ q="How do I cancel my subscription?">
                            <p>You can self-cancel inside the app:</p>
                            <ul>
                                <li>Open <b>Profile · Subscription</b> (or tap "Manage" on the home screen).</li>
                                <li>Tap <b>Cancel subscription</b>.</li>
                                <li>Choose <b>Cancel now</b> (immediate, prorated refund where applicable) or <b>Cancel at period end</b> (keep access until the renewal date).</li>
                            </ul>
                            {iOSApp ? (
                                <p className="mt-2">If you subscribed via the Apple App Store, you can also cancel from <b>iPhone Settings → [Your Name] → Subscriptions</b>.</p>
                            ) : (
                                <p className="mt-2">If you subscribed via Apple App Store, you can also cancel from <b>iPhone Settings → [Your Name] → Subscriptions</b>. On Google Play: <b>Play Store → Profile icon → Payments &amp; subscriptions</b>.</p>
                            )}
                        </FAQ>
                        <FAQ q="How do I restore purchases on a new phone?">
                            {iOSApp ? (
                                <p>Sign in with the <b>same Apple ID</b> used for the original purchase, then in the Femcare app go to <b>Premium screen → Restore purchases</b>. Your subscription will re-sync within a few seconds.</p>
                            ) : (
                                <p>Sign in with the <b>same Apple ID / Google account</b> used for the original purchase, then in the Femcare app go to <b>Premium screen → Restore purchases</b>. Your subscription will re-sync within a few seconds.</p>
                            )}
                        </FAQ>
                        <FAQ q="Can I get a refund?">
                            <p>Refunds are handled by the app store you purchased from:</p>
                            <ul>
                                <li><b>Apple App Store:</b> visit <a href="https://reportaproblem.apple.com/" target="_blank" rel="noopener noreferrer">reportaproblem.apple.com</a> within 90 days of purchase.</li>
                                {!iOSApp && (
                                    <li><b>Google Play:</b> open the Play Store → Account → Payment history → tap your purchase → <b>Request refund</b> (within 48 hours typically auto-approved).</li>
                                )}
                                {!iOSApp && (
                                    <li><b>Razorpay/Web purchases (India):</b> email <a href="mailto:support@femcare.life">support@femcare.life</a> within 7 days of payment. Refunds processed within 5–7 business days to the original payment method.</li>
                                )}
                            </ul>
                        </FAQ>
                        <FAQ q="Can I upgrade from Monthly to Yearly?">
                            <p>Yes — from <b>Premium screen</b>, tap the Yearly plan and confirm. Your existing Monthly subscription is prorated and the difference is applied to the Yearly billing immediately.</p>
                        </FAQ>
                        <FAQ q="I was charged but Premium isn't active?">
                            <p>Usually a payment confirmation race. Try:</p>
                            <ul>
                                <li>Force-quit and reopen Femcare.</li>
                                <li>Pull-to-refresh the Premium screen.</li>
                                <li>Tap <b>Restore purchases</b>.</li>
                            </ul>
                            <p className="mt-2">If it still doesn't unlock after 10 minutes, email <a href="mailto:support@femcare.life">support@femcare.life</a> with your order/transaction ID and we'll fix it within 24 hours.</p>
                        </FAQ>
                    </Section>

                    {/* PRIVACY */}
                    <Section id="privacy" icon={Shield} title="Privacy & data">
                        <FAQ q="Where is my data stored?">
                            <p>Your data is stored on secure servers with industry-standard encryption (TLS in transit, AES-256 at rest). For our full data practices and your rights, see our <Link to="/privacy">Privacy Policy</Link>.</p>
                        </FAQ>
                        <FAQ q="How do I export my data?">
                            <p>Open <b>Profile · Privacy &amp; data → Export my data</b>. We'll email you a downloadable JSON archive of every log entry, profile field, and report — usually within 24 hours.</p>
                        </FAQ>
                        <FAQ q="How do I delete my account?">
                            <p>Open <b>Profile · Privacy &amp; data → Delete my account</b>, confirm with your password, and we'll permanently erase every personal record within 30 days (some anonymous billing receipts are retained for tax compliance).</p>
                            <Callout><b>This is irreversible.</b> Export your data first if you'd like a copy.</Callout>
                        </FAQ>
                        <FAQ q="Do you share my health data with advertisers?">
                            <p><b>Never.</b> We do not sell, rent, or share your personal health data with advertisers or data brokers. See our <Link to="/privacy">Privacy Policy</Link> for the full list of third parties we work with (payment processors and security infra only).</p>
                        </FAQ>
                    </Section>

                    {/* TROUBLESHOOTING */}
                    <Section id="troubleshooting" icon={Wrench} title="Troubleshooting">
                        <FAQ q="Reminders aren't arriving">
                            <ul>
                                <li><b>iOS:</b> Settings → Notifications → Femcare → enable Allow Notifications, Sounds, and Banners.</li>
                                {!iOSApp && (
                                    <li><b>Android:</b> Settings → Apps → Femcare → Notifications → enable. Also disable battery optimisation for Femcare.</li>
                                )}
                                <li>In Femcare: <b>Profile · Reminders &amp; Notifications</b> — verify reminders are toggled on and times are correct for your timezone.</li>
                            </ul>
                        </FAQ>
                        <FAQ q="I forgot my password">
                            <p>On the login screen, tap <b>Forgot password</b>, enter your email, and we'll send a reset link valid for 30 minutes. Check spam/promotions if you don't see it.</p>
                        </FAQ>
                        <FAQ q="My calendar predictions look wrong">
                            <p>Try:</p>
                            <ul>
                                <li>Check that your <b>last period start date</b> in Profile is correct.</li>
                                <li>Make sure <b>average cycle length</b> reflects your actual average.</li>
                                <li>Log at least 2 full cycles for predictions to stabilise.</li>
                                <li>If you recently came off hormonal contraception, expect 2–3 months of variable cycles.</li>
                            </ul>
                        </FAQ>
                        <FAQ q={iOSApp ? "Apple Health not syncing" : "Apple Health / Health Connect not syncing"}>
                            <ul>
                                <li><b>iOS:</b> Settings → Health → Data Access &amp; Devices → Femcare → Turn On All. Then re-open Femcare and pull-to-refresh.</li>
                                {!iOSApp && (
                                    <li><b>Android:</b> Health Connect → Apps and permissions → Femcare → grant all read permissions.</li>
                                )}
                                <li>In Femcare: <b>Profile · Wearables</b> → tap <b>Re-sync now</b>.</li>
                            </ul>
                        </FAQ>
                        <FAQ q="App is slow or crashing">
                            <ul>
                                {iOSApp ? (
                                    <li>Update to the latest version from the App Store.</li>
                                ) : (
                                    <li>Update to the latest version from the App Store / Play Store.</li>
                                )}
                                <li>Restart your phone.</li>
                                <li>Free up at least 500 MB of storage.</li>
                                <li>Still slow? Email <a href="mailto:support@femcare.life">support@femcare.life</a> with your device model and OS version — we'll investigate immediately.</li>
                            </ul>
                        </FAQ>
                        <FAQ q="I'm not getting verification emails">
                            <p>Check your spam, promotions, and "all mail" folders. If you still don't see it, request a new one from the login/signup screen, or contact <a href="mailto:support@femcare.life">support@femcare.life</a> and we'll verify your account manually.</p>
                        </FAQ>
                    </Section>

                    {/* CONTACT */}
                    <section id="contact" className="scroll-mt-28 mt-4" data-testid="support-section-contact">
                        <div className="bg-gradient-to-br from-femcare-pink via-femcare-accent to-femcare-purple text-white rounded-3xl p-8 md:p-12 text-center">
                            <h3 className="editorial-headline text-4xl md:text-5xl mb-3">Still need help?</h3>
                            <p className="text-pink-50/90 mb-7 text-lg">Our care team replies within 24 hours, Mon–Fri.</p>
                            <div className="flex flex-wrap justify-center gap-3 mb-5">
                                <a
                                    href="mailto:support@femcare.life"
                                    className="inline-flex items-center gap-2 bg-white text-femcare-pink hover:bg-femcare-petal rounded-full px-6 py-3 text-sm font-medium transition"
                                    data-testid="support-email-cta"
                                >
                                    <Mail className="w-4 h-4" /> Email support
                                </a>
                                <Link
                                    to="/privacy"
                                    className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 rounded-full px-6 py-3 text-sm font-medium transition"
                                >
                                    <Shield className="w-4 h-4" /> Privacy Policy
                                </Link>
                            </div>
                            <p className="text-xs text-pink-100/70 tracking-wide">support@femcare.life · Mon–Fri, 9 AM – 6 PM IST</p>
                        </div>
                    </section>

                </article>
            </div>
        </div>
    );
}
